/**
 * Global Search – Type definitions
 *
 * Covers platform-search full-text results, typeahead suggestions,
 * and ai-orchestrator chat/streaming payloads.
 */

// ─── Entity types ────────────────────────────────────────────────────────────

export type SearchEntityType =
  | 'customer'
  | 'account'
  | 'transaction'
  | 'loan'
  | 'document';

export const ENTITY_ICONS: Record<SearchEntityType, string> = {
  customer: '👤',
  account: '🏦',
  transaction: '💳',
  loan: '📝',
  document: '📄',
};

export const ENTITY_LABELS: Record<SearchEntityType, string> = {
  customer: 'Customer',
  account: 'Account',
  transaction: 'Transaction',
  loan: 'Loan',
  document: 'Document',
};

// ─── Search request / response ───────────────────────────────────────────────

export interface SearchFiltersPayload {
  entityTypes?: SearchEntityType[];
  dateFrom?: string;
  dateTo?: string;
  institutionId?: string;
  status?: string;
}

export interface SearchRequest {
  query: string;
  filters?: SearchFiltersPayload;
  page?: number;
  pageSize?: number;
}

export interface TypeaheadRequest {
  query: string;
  limit?: number;
}

export interface SearchResultItem {
  id: string;
  entityType: SearchEntityType;
  title: string;
  subtitle: string;
  description: string;
  score: number;
  highlights: Record<string, string[]>;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface SearchFacet {
  field: string;
  values: { value: string; count: number }[];
}

export interface SearchResponse {
  results: SearchResultItem[];
  total: number;
  page: number;
  pageSize: number;
  facets: SearchFacet[];
  queryTimeMs: number;
}

export interface TypeaheadSuggestion {
  id: string;
  entityType: SearchEntityType;
  title: string;
  subtitle: string;
  score: number;
}

export interface TypeaheadResponse {
  suggestions: TypeaheadSuggestion[];
  queryTimeMs: number;
}

// ─── AI chat request / response ──────────────────────────────────────────────

export interface AIChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
}

export interface AIChatRequest {
  message: string;
  conversationId?: string;
  context?: {
    currentPage?: string;
    selectedEntityId?: string;
  };
}

export interface AIChatResponse {
  conversationId: string;
  message: AIChatMessage;
  sources: SearchResultItem[];
}

/** Server-Sent Event chunk from /chat/stream */
export interface AIChatStreamEvent {
  type: 'token' | 'source' | 'done' | 'error';
  data: string;
}

// ─── Recent searches ─────────────────────────────────────────────────────────

export interface RecentSearch {
  query: string;
  timestamp: number;
  resultCount: number;
}

// ─── Store state ─────────────────────────────────────────────────────────────

export interface SearchStoreState {
  // Query
  query: string;
  setQuery: (q: string) => void;

  // Filters
  filters: SearchFiltersPayload;
  setFilters: (f: SearchFiltersPayload) => void;
  resetFilters: () => void;

  // Full-text results
  results: SearchResultItem[];
  total: number;
  page: number;
  pageSize: number;
  facets: SearchFacet[];
  queryTimeMs: number;
  resultsLoading: boolean;
  resultsError: string | null;
  fetchResults: (query: string, filters?: SearchFiltersPayload, page?: number) => Promise<void>;

  // Typeahead
  suggestions: TypeaheadSuggestion[];
  suggestionsLoading: boolean;
  fetchSuggestions: (query: string) => Promise<void>;
  clearSuggestions: () => void;

  // AI chat
  chatMessages: AIChatMessage[];
  chatConversationId: string | null;
  chatSources: SearchResultItem[];
  chatLoading: boolean;
  chatStreaming: boolean;
  chatError: string | null;
  sendChatMessage: (message: string) => Promise<void>;
  streamChatMessage: (message: string) => void;
  abortChatStream: () => void;
  clearChat: () => void;

  // AI drawer
  aiDrawerOpen: boolean;
  setAiDrawerOpen: (open: boolean) => void;

  // Recent searches
  recentSearches: RecentSearch[];
  addRecentSearch: (query: string, resultCount: number) => void;
  clearRecentSearches: () => void;

  // Keyboard navigation
  highlightedIndex: number;
  setHighlightedIndex: (i: number) => void;
}
